"use client";
import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronDown, ChevronUp, Check, Filter, X } from "lucide-react";
import { categories, Project } from "@/components/Project/ProjectData";
import ProjectCard from "@/components/Project/ProjectCard";

interface ProjectsPageClientProps {
  projects: Project[];
}

const filterCategories = (projects: Project[]) => ({
  categories: {
    title: "Categories",
    options: categories,
  },
  technologies: {
    title: "Technologies",
    options: Array.from(
      new Set(projects.flatMap((project) => project.technologies))
    ),
  },
});

// Premium Custom Checkbox Component
const CustomCheckbox = ({ checked, onChange, label, id }: {
  checked: boolean;
  onChange: () => void;
  label: string;
  id: string;
}) => {
  return (
    <div
      className={`
        group flex items-center space-x-3 py-2 px-3 rounded-md
        cursor-pointer
        ${checked 
          ? 'text-primary bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 dark:hover:bg-primary/30' 
          : 'text-gray-600 dark:text-gray-400 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/30 dark:hover:bg-gray-700/50'
        }
      `}
      onClick={onChange}
    >
      <div className="relative flex items-center">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only"
        />
        <div
          className={`
            flex items-center justify-center w-4 h-4 rounded border
            ${checked
              ? 'bg-primary border-primary'
              : 'border-gray-300 dark:border-gray-600'
            }
          `}
        >
          <Check 
            className={`w-3 h-3 text-white ${checked ? 'opacity-100' : 'opacity-0'}`}
            strokeWidth={2.5}
          />
        </div>
      </div>
      <label
        htmlFor={id}
        className={`
          text-sm cursor-pointer select-none
          ${checked ? 'font-medium' : ''}
        `}
      >
        {label}
      </label>
    </div>
  );
};

const ProjectsPageClient = ({ projects }: ProjectsPageClientProps) => {
  const [filters, setFilters] = useState({
    categories: ["All"],
    technologies: [],
  });

  const [expandedCategories, setExpandedCategories] = useState<string[]>(["categories"]);
  const [sidebarHeight, setSidebarHeight] = useState<number>(0);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Calculate and set fixed height for sidebar to prevent jumping
  useEffect(() => {
    if (sidebarRef.current) {
      const height = sidebarRef.current.scrollHeight;
      setSidebarHeight(height);
    }
  }, [expandedCategories, filters]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleFilter = (category: string, value: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      const currentValues = [...newFilters[category as keyof typeof prev]];

      if (category === "categories" && value === "All") {
        newFilters[category] = ["All"];
        return newFilters;
      }

      if (category === "categories" && currentValues.includes("All")) {
        currentValues.splice(currentValues.indexOf("All"), 1);
      }

      const index = currentValues.indexOf(value);
      if (index === -1) {
        currentValues.push(value);
      } else {
        currentValues.splice(index, 1);
      }

      if (category === "categories" && currentValues.length === 0) {
        currentValues.push("All");
      }

      newFilters[category as keyof typeof prev] = currentValues;
      return newFilters;
    });
  };

  const clearAllFilters = () => {
    setFilters({
      categories: ["All"],
      technologies: [],
    });
  };

  const filterCategoriesData = filterCategories(projects);

  const filteredProjects = projects.filter((project) => {
    const categoryMatch =
      filters.categories.includes("All") ||
      project.categories.some((c) => filters.categories.includes(c));
    const techMatch =
      filters.technologies.length === 0 ||
      project.technologies.some((t) => filters.technologies.includes(t));
    return categoryMatch && techMatch;
  });

  const getFilterCount = (category: string) => {
    const count = filters[category as keyof typeof filters].length;
    if (category === "categories" && filters.categories.includes("All"))
      return 0;
    return count;
  };

  const getTotalActiveFilters = () => {
    return getFilterCount("categories") + getFilterCount("technologies");
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <h1 className="mt-16 pt-8 mb-4 text-4xl font-bold bg-clip-text text-gray-900 dark:text-white">
          Explore the Future-Already Built
        </h1>
        <p className="mb-8 text-lg text-gray-500 dark:text-gray-400 max-w-3xl">
          Step into a world where AI meets imagination. Our projects aren&apos;t just solutions—they&apos;re
          the building blocks of a smarter, more intuitive future.
        </p>

        <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
          {/* Sidebar Filters - Using fixed positioning approach */}
          <div className="w-full lg:w-64 lg:flex-shrink-0">
            {/* Fixed height wrapper to reserve space */}
            <div 
              className="lg:relative"
              style={{ minHeight: sidebarHeight || 'auto' }}
            >
              {/* Sidebar content with non-sticky positioning on mobile, absolute on desktop */}
              <div 
                ref={sidebarRef}
                className="w-full lg:absolute lg:top-0 lg:left-0 lg:w-64"
              >
                <div className="rounded-lg border border-gray-200/30 dark:border-gray-800/30 p-4 backdrop-blur-sm bg-white dark:bg-transparent shadow-xl dark:shadow-none">
                  {/* Filter Header with Clear All */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Filter By
                      </h2>
                    </div>
                    {getTotalActiveFilters() > 0 && (
                      <button
                        onClick={clearAllFilters}
                        className="text-sm text-gray-500 hover:text-primary transition-colors duration-200 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" />
                        Clear all
                      </button>
                    )}
                  </div>

                  {/* Filter Categories */}
                  <div className="space-y-6 pb-2">
                    {Object.entries(filterCategoriesData).map(([key, category], index) => (
                      <div key={key} className="space-y-3">
                        {index > 0 && (
                          <div className="h-px bg-gray-200 dark:bg-gray-700 mt-3 mb-3" />
                        )}
                        
                        <button
                          onClick={() => toggleCategory(key)}
                          className="flex w-full items-center justify-between group py-1"
                        >
                          <h3 className="text-base font-medium text-gray-900 dark:text-white text-left">
                            {category.title}
                          </h3>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {getFilterCount(key) > 0 && (
                              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary min-w-[1.5rem] text-center">
                                {getFilterCount(key)}
                              </span>
                            )}
                            <ChevronDown 
                              className={`h-4 w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
                                expandedCategories.includes(key) ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        </button>

                        {/* Expandable filter options */}
                        {expandedCategories.includes(key) && (
                          <div className="space-y-1 pt-1 animate-in slide-in-from-top-2 duration-200">
                            {category.options.map((option, idx) => (
                              <CustomCheckbox
                                key={`${key}-${option}-${idx}`}
                                id={`${key}-${option}-${idx}`}
                                checked={filters[key as keyof typeof filters].includes(option)}
                                onChange={() => toggleFilter(key, option)}
                                label={option}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Projects Grid */}
          <div className="flex-1 min-w-0 lg:ml-8">
            <div className="mb-6 text-sm text-gray-600 dark:text-gray-400">
              Showing {filteredProjects.length} of {projects.length} projects
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3 place-items-center md:place-items-start">
              {filteredProjects.map((project) => (
                <ProjectCard key={project.title} project={project} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPageClient;
